import {
  Box,
  Collapse,
  Typography,
  IconButton,
  Divider,
  Grid,
  Skeleton,
  Chip,
} from "@mui/material";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import Modal from "modules/Modal/Modal";
import ScheduleJobPhase from "modules/jobs/ScheduleJobPhase/ScheduleJobPhase";
import View from "modules/jobs/View";
import useGetJobLazy from "hooks/jobs/useGetJobLazy";
import React, { useState, useMemo, useCallback } from "react";
import { JobStatus, Job } from "generated/graphql";
import formatCentsToUSD from "utils/money/formatCentsToUSD";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import StatusChip from "./StatusChip";
import { useCalendarContext } from "modules/jobs/JobsSchedule/CalendarContext";
import { groupPhasesByProductGroup } from "./groupPhases";
import JobCardPhaseRow from "./JobCardPhaseRow";

dayjs.extend(utc);

dayjs.extend(utc);

interface JobCardProps {
  job: any;
}

export default function JobCard({ job }: JobCardProps) {
  const [expanded, setExpanded] = useState(false);
  const [selectedPhaseId, setSelectedPhaseId] = useState<string | null>(null);
  const [showJobInfo, setShowJobInfo] = useState(false);
  const [getJobDetails, { job: jobDetails }] = useGetJobLazy();
  const { triggerCalendarUpdate } = useCalendarContext();

  const handleJobClick = useCallback(() => {
    setExpanded(!expanded);
  }, [expanded]);

  const handlePhaseClick = useCallback((e: React.MouseEvent, phaseId: string) => {
    e.stopPropagation();
    setSelectedPhaseId(phaseId);
  }, []);

  const handleClosePhaseModal = useCallback(() => {
    setSelectedPhaseId(null);
  }, []);

  const handleJobIdClick = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      getJobDetails(job.id);
      setShowJobInfo(true);
    },
    [job.id, getJobDetails]
  );

  const handleCloseJobInfo = useCallback(() => {
    setShowJobInfo(false);
  }, []);

  const handleScheduleSuccess = useCallback(() => {
    triggerCalendarUpdate();
  }, [triggerCalendarUpdate]);

  const hasUnscheduledPhases = useMemo(() => {
    return job.jobPhases?.some((phase: any) => phase.status === "TO_BE_SCHEDULED");
  }, [job.jobPhases]);

  const maxUnscheduledPhaseDuration = useMemo(() => {
    if (!job.jobPhases) return 0;
    const unscheduledPhases = job.jobPhases.filter(
      (phase: any) => phase.status === "TO_BE_SCHEDULED"
    );

    const durations = unscheduledPhases.map((phase: any) => phase.jobDuration || 0);
    return durations.length > 0 ? Math.max(...durations) : 0;
  }, [job.jobPhases]);

  const getStatusColor = (status: JobStatus) => {
    switch (status) {
      case JobStatus.SCHEDULED:
        return "success";
      case JobStatus.TO_BE_SCHEDULED:
        return "warning";
      case JobStatus.IN_PROGRESS:
        return "info";
      case JobStatus.WORK_COMPLETE:
        return "info";
      case JobStatus.COMPLETE:
        return "primary";
      case JobStatus.JOB_COSTING:
        return "secondary";
      case JobStatus.READY_TO_BILL:
        return "secondary";
      default:
        return "default";
    }
  };

  return (
    <>
      <MDBox
        ml={2}
        mr={2}
        draggable={hasUnscheduledPhases}
        data-draggable={hasUnscheduledPhases ? "true" : "false"}
        data-phase-data={
          hasUnscheduledPhases
            ? JSON.stringify({
                type: "job",
                jobId: job.id,
                jobAddress: job.address?.line1 || "Unknown Address",
                jobExternalId: job.externalId,
                duration: maxUnscheduledPhaseDuration,
                total: job.proposedOverallTotal,
                phasesCount:
                  job.jobPhases?.filter((p: any) => p.status === "TO_BE_SCHEDULED").length || 0,
              })
            : ""
        }
        onDragStart={(e) => {
          if (hasUnscheduledPhases) {
            const jobData = {
              type: "job",
              jobId: job.id,
              jobAddress: job.address?.line1 || "Unknown Address",
              jobExternalId: job.externalId,
              duration: maxUnscheduledPhaseDuration,
              total: job.proposedOverallTotal,
              phasesCount:
                job.jobPhases?.filter((p: any) => p.status === "TO_BE_SCHEDULED").length || 0,
            };
            e.dataTransfer.setData("text/plain", JSON.stringify(jobData));
            e.dataTransfer.effectAllowed = "copy";
          }
        }}
        sx={{
          cursor: hasUnscheduledPhases ? "grab" : "pointer",
          "&:active": {
            cursor: hasUnscheduledPhases ? "grabbing" : "pointer",
          },
        }}
        onClick={handleJobClick}
      >
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            {hasUnscheduledPhases && (
              <DragIndicatorIcon
                sx={{
                  fontSize: "1rem",
                  color: "#9e9e9e",
                  pointerEvents: "none",
                }}
              />
            )}
            <Typography variant="body2" sx={{ lineHeight: 1.4 }}>
              JOB ID :{" "}
              <Typography
                component="span"
                variant="body2"
                onClick={handleJobIdClick}
                sx={{
                  lineHeight: 1.4,
                  color: "#1976d2",
                  cursor: "pointer",
                  textDecoration: "underline",
                  "&:hover": {
                    color: "#1565c0",
                  },
                }}
              >
                {job.externalId}
              </Typography>
            </Typography>
            <StatusChip status={job.status} />
          </Box>
          <IconButton
            size="small"
            sx={{
              pointerEvents: "none",
            }}
          >
            {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </IconButton>
        </Box>

        {(job.company?.name || job.proposalStage?.name) && (
          <Typography variant="body2" sx={{ display: "block", mb: 1, lineHeight: 1.4 }}>
            {job.company?.name} - {job.proposalStage?.name}
          </Typography>
        )}

        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
          {job.address?.city && job.address?.state
            ? `${job.address.city}, ${job.address.state}`
            : ""}
        </Typography>

        <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
          Won Date
        </Typography>
        <Typography variant="body2" fontWeight="bold" sx={{ mb: 1, fontSize: "0.875rem" }}>
          {job.proposal?.wonAt ? dayjs(job.proposal.wonAt).format("MM/DD/YYYY") : "Not set"}
        </Typography>

        <Typography sx={{ fontSize: "0.875rem" }}>Job Total</Typography>
        <Chip
          label={`JOB ID: ${job.externalId}`}
          size="small"
          sx={{
            bgcolor: "#f2f3f5",
            color: "#6b7280",
            fontWeight: "bold",
            fontSize: "0.7rem",
            float: "right",
          }}
        />
        <Typography
          variant="body2"
          fontWeight="bold"
          sx={{ mb: 1, fontSize: "0.875rem", color: "#0d804a" }}
        >
          {formatCentsToUSD(parseInt(job.proposedOverallTotal))}
        </Typography>

        <Collapse in={expanded} timeout="auto" unmountOnExit>
          <Box sx={{ mt: 1 }}>
            <Divider sx={{ mb: 1 }} />
            <Typography variant="body2" fontWeight="bold" sx={{ pl: 1, fontSize: "0.575rem" }}>
              Project details
            </Typography>
            <Typography variant="body2" sx={{ pl: 1, mb: 2, fontSize: "0.575rem" }}>
              Drag phase to schedule it on the calendar
            </Typography>
            {job.jobPhases?.length > 0 ? (
              groupPhasesByProductGroup(job.jobPhases).map((item, idx) => {
                if (item.kind === "group") {
                  return (
                    <Box key={`group-${idx}`} sx={{ mb: 1 }}>
                      <Box
                        sx={{
                          backgroundColor: "#f0f2f5",
                          borderRadius: "4px",
                          px: 1,
                          pt: 0.25,
                          pb: 0.5,
                          mb: 0.5,
                        }}
                      >
                        <Typography variant="caption" fontWeight="bold" sx={{ fontSize: "0.7rem" }}>
                          {item.groupName}
                        </Typography>
                      </Box>
                      {item.phases.map((phase: any) => (
                        <JobCardPhaseRow
                          key={phase.id}
                          phase={phase}
                          job={job}
                          indented
                          handlePhaseClick={handlePhaseClick}
                        />
                      ))}
                      <Divider sx={{ mb: 1 }} />
                    </Box>
                  );
                }

                return (
                  <Box key={item.phase.id}>
                    <JobCardPhaseRow
                      phase={item.phase}
                      job={job}
                      handlePhaseClick={handlePhaseClick}
                    />
                    <Divider sx={{ mb: 1 }} />
                  </Box>
                );
              })
            ) : (
              <Box sx={{ pl: 1, py: 2 }}>
                <Typography variant="caption" color="text.secondary">
                  No phases available for this job
                </Typography>
              </Box>
            )}
          </Box>
        </Collapse>
      </MDBox>

      <Modal open={!!selectedPhaseId} onClose={handleClosePhaseModal}>
        {selectedPhaseId && (
          <ScheduleJobPhase
            id={selectedPhaseId}
            onClose={handleClosePhaseModal}
            onScheduleSuccess={handleScheduleSuccess}
          />
        )}
      </Modal>

      <Modal open={showJobInfo} onClose={handleCloseJobInfo}>
        <MDBox my={3} px="20px" overflow="auto">
          <MDBox mb={6}>
            <Grid container spacing={3} alignItems="center">
              <Grid item xs={12} lg={6}>
                <MDTypography variant="h4" fontWeight="medium">
                  Job Info {jobDetails && `#${jobDetails.externalId}`}
                </MDTypography>
              </Grid>
            </Grid>
          </MDBox>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              {jobDetails && <View job={jobDetails as Job} />}
              {!jobDetails && <Skeleton />}
            </Grid>
          </Grid>
        </MDBox>
      </Modal>
    </>
  );
}
